import React from 'react';
import { Check, Cpu, Gauge, Image, Search, Server, Sparkles, Video, Wallet } from 'lucide-react';
import { AuthProvider } from '../lib/auth-context';
import { useSubscribeFlow } from './SubscribeFlow';

// The pricing page is usage-based first: one prepaid credit balance covers
// agents, the model gateway, GPU hosting, rented compute, and search. Real
// numbers come from GET /api/pricing (single source of truth in server/
// pricing.go); the static tables below are a deterministic fallback so the
// prerendered HTML paints real prices before the fetch resolves. Subscription
// plans (GET /api/plans) live further down as an optional monthly bundle.

// ---- usage-based pricing -------------------------------------------------

type ModelPrice = {
  id: string;
  providerName: string;
  logo?: string;
  inputPer1m: number;
  outputPer1m: number;
};

type ComputePrice = {
  id: string;
  label: string;
  kind: string;
  vcpu: number;
  ramGb: number;
  gpu?: string;
  gpuCount?: number;
  vramGb?: number;
  priceHourText: string;
  priceSecondText?: string;
  comingSoon?: boolean;
};

type SearchPrice = { id: string; label: string; unit: string; priceUsd: number; note: string };
type MediaPrice = {
  image?: { label: string; unit: string; credits: number; priceUsd: number; note: string };
  video?: { label: string; unit: string; minCredits: number; exampleUsd: number; exampleUnit: string; note: string };
};

type PricingData = {
  creditUnitUsd: number;
  models: ModelPrice[];
  compute: ComputePrice[];
  search: SearchPrice[];
  media?: MediaPrice;
};

// Static fallback mirrors server/pricing.go output (provider cost with the
// platform margin already applied) so SSR renders real prices, then the fetch
// refreshes them. Keep in rough sync with the catalogue; the live fetch wins.
const STATIC_PRICING: PricingData = {
  creditUnitUsd: 0.001,
  models: [
    { id: 'openpaths/auto-fast', providerName: 'DeepSeek', inputPer1m: 0.168, outputPer1m: 0.336 },
    { id: 'openpaths/auto-cheap', providerName: 'OpenAI', inputPer1m: 0.24, outputPer1m: 1.5 },
    { id: 'openpaths/auto', providerName: 'Google Gemini', inputPer1m: 1.8, outputPer1m: 10.8 },
    { id: 'openpaths/auto-code', providerName: 'OpenAI', inputPer1m: 6.0, outputPer1m: 36.0 },
    { id: 'openpaths/auto-reasoning', providerName: 'OpenAI', inputPer1m: 0.9, outputPer1m: 5.4 },
    { id: 'claude-opus-5', providerName: 'Anthropic', inputPer1m: 6.0, outputPer1m: 30.0 },
    { id: 'claude-sonnet-5-5', providerName: 'Anthropic', inputPer1m: 3.6, outputPer1m: 18.0 },
    { id: 'claude-sonnet-5', providerName: 'Anthropic', inputPer1m: 3.6, outputPer1m: 18.0 },
    { id: 'claude-haiku-4-5-20251001', providerName: 'Anthropic', inputPer1m: 1.2, outputPer1m: 6.0 },
    { id: 'gpt-5.4', providerName: 'OpenAI', inputPer1m: 3.0, outputPer1m: 18.0 },
    { id: 'gpt-5-mini', providerName: 'OpenAI', inputPer1m: 0.3, outputPer1m: 2.4 },
    { id: 'gemini-2.5-pro', providerName: 'Google Gemini', inputPer1m: 1.5, outputPer1m: 12.0 },
    { id: 'gemini-3.8-flash', providerName: 'Google Gemini', inputPer1m: 0.9, outputPer1m: 4.5 },
    { id: 'deepseek-chat', providerName: 'DeepSeek', inputPer1m: 0.336, outputPer1m: 0.504 },
    { id: 'grok-4.5', providerName: 'xAI', inputPer1m: 2.4, outputPer1m: 7.2 },
    { id: 'grok-4.3', providerName: 'xAI', inputPer1m: 1.5, outputPer1m: 3.0 },
    { id: 'llama-3.3-70b-versatile', providerName: 'Groq', inputPer1m: 0.708, outputPer1m: 0.948 },
  ],
  compute: [
    { id: 'cpx21', label: 'CPU Small', kind: 'cpu', vcpu: 3, ramGb: 4, priceHourText: '$0.010', priceSecondText: '$0.000003' },
    { id: 'cpx32', label: 'CPU Medium', kind: 'cpu', vcpu: 4, ramGb: 8, priceHourText: '$0.018', priceSecondText: '$0.000005' },
    { id: 'cpx41', label: 'CPU Large', kind: 'cpu', vcpu: 8, ramGb: 16, priceHourText: '$0.038', priceSecondText: '$0.000010' },
    { id: 'win-cpx32', label: 'Windows CPU Medium', kind: 'cpu', vcpu: 4, ramGb: 8, priceHourText: '$0.018', priceSecondText: '$0.000005' },
    { id: 'win-cpx41', label: 'Windows CPU Large', kind: 'cpu', vcpu: 8, ramGb: 16, priceHourText: '$0.038', priceSecondText: '$0.000010' },
    { id: 'gpu-a100', label: 'A100 80GB', kind: 'gpu', vcpu: 12, ramGb: 80, gpu: 'A100-80GB', gpuCount: 1, vramGb: 80, priceHourText: '$2.624', priceSecondText: '$0.000729' },
    { id: 'gpu-h100', label: 'H100 80GB', kind: 'gpu', vcpu: 16, ramGb: 80, gpu: 'H100-80GB', gpuCount: 1, vramGb: 80, priceHourText: '$4.784', priceSecondText: '$0.001329' },
    { id: 'gpu-h200', label: 'H200 141GB', kind: 'gpu', vcpu: 16, ramGb: 141, gpu: 'H200-141GB', gpuCount: 1, vramGb: 141, priceHourText: '$6.384', priceSecondText: '$0.001773' },
    { id: 'gpu-a40', label: 'A40 48GB', kind: 'gpu', vcpu: 9, ramGb: 48, gpu: 'A40', gpuCount: 1, vramGb: 48, priceHourText: '$0.640', priceSecondText: '$0.000178' },
    { id: 'gpu-l40s', label: 'L40S', kind: 'gpu', vcpu: 8, ramGb: 48, gpu: 'L40S', gpuCount: 1, vramGb: 48, priceHourText: '$1.376', priceSecondText: '$0.000382' },
    { id: 'gpu-rtx3090', label: 'RTX 3090 24GB', kind: 'gpu', vcpu: 8, ramGb: 24, gpu: 'RTX-3090', gpuCount: 1, vramGb: 24, priceHourText: '$0.352', priceSecondText: '$0.000098' },
    { id: 'gpu-rtx4090', label: 'RTX 4090 24GB', kind: 'gpu', vcpu: 8, ramGb: 24, gpu: 'RTX-4090', gpuCount: 1, vramGb: 24, priceHourText: '$0.544', priceSecondText: '$0.000151' },
    { id: 'gpu-t4', label: 'T4 16GB', kind: 'gpu', vcpu: 4, ramGb: 16, gpu: 'T4', gpuCount: 1, vramGb: 16, priceHourText: '$0.304', priceSecondText: '$0.000084' },
    { id: 'tpu-v5e', label: 'TPU v5e', kind: 'gpu', vcpu: 24, ramGb: 48, gpu: 'TPU-v5e', gpuCount: 1, vramGb: 16, priceHourText: '$2.304', priceSecondText: '$0.000640', comingSoon: true },
    { id: 'tpu-v6e', label: 'TPU v6e (Trillium)', kind: 'gpu', vcpu: 44, ramGb: 176, gpu: 'TPU-v6e', gpuCount: 1, vramGb: 32, priceHourText: '$5.184', priceSecondText: '$0.001440', comingSoon: true },
  ],
  search: [
    { id: 'web', label: 'Web search', unit: '1,000 requests', priceUsd: 7.7, note: 'Live web results with citations' },
    { id: 'papers', label: 'Paper search', unit: '1,000 requests', priceUsd: 1.0, note: 'Academic + preprint search' },
  ],
  media: {
    image: {
      label: 'Responsive image set',
      unit: 'set up to 8 generated variants',
      credits: 1,
      priceUsd: 0.001,
      note: 'Dynamic reads are cacheable; generated artifact variants are billed once.',
    },
    video: {
      label: 'AV1 video ladder',
      unit: 'wall-clock encode time',
      minCredits: 2,
      exampleUsd: 0.057,
      exampleUnit: 'one minute of encoder time at the default GPU rate',
      note: 'Uses AV1 NVENC when available, then CPU AV1/VP9/H.264 fallback.',
    },
  },
};

// money formats a per-1M-token or flat dollar price compactly: sub-dollar prices
// keep three significant decimals, everything else two.
function money(n: number): string {
  if (n === 0) return 'included';
  return `$${n.toFixed(n < 1 ? 3 : 2)}`;
}

const HOW_IT_WORKS = [
  { icon: Wallet, title: 'Prepay credits', body: 'Top up once. 1 credit = $0.001. No minimums, no monthly commitment.' },
  { icon: Gauge, title: 'Use anything', body: 'Agents, the model gateway, GPU model hosting, rented compute, and search all draw from the same balance.' },
  { icon: Check, title: 'Pay for what you use', body: 'Metered to the token and to the second. Set spend caps per project, key, app, or agent run.' },
];

const UsageBasedPricing: React.FC = () => {
  const [data, setData] = React.useState<PricingData>(STATIC_PRICING);

  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/pricing')
      .then((r) => (r.ok ? r.json() : null))
      .then((live) => {
        if (cancelled || !live?.models?.length) return;
        setData({
          creditUnitUsd: live.creditUnitUsd ?? STATIC_PRICING.creditUnitUsd,
          models: live.models,
          compute: live.compute?.length ? live.compute : STATIC_PRICING.compute,
          search: live.search?.length ? live.search : STATIC_PRICING.search,
          media: live.media || STATIC_PRICING.media,
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section data-testid="usage-pricing" className="mt-10 border-t border-white/10 pt-8">
      <div className="mb-3 flex items-center gap-2">
        <Sparkles size={16} className="text-white/48" />
        <h2 className="font-display text-2xl font-bold">Pay as you go from one prepaid balance</h2>
      </div>
      <p className="mb-5 max-w-3xl text-sm leading-6 text-white/54">
        Everything on app.nz bills from one prepaid credit balance: coding agents, the model gateway, GPU model
        hosting, rented CPU/GPU compute, web search, and paper search. Top up once. No subscription required.
      </p>

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        {HOW_IT_WORKS.map((step) => {
          const StepIcon = step.icon;
          return (
            <div key={step.title} className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
              <div className="mb-2 flex items-center gap-2">
                <StepIcon size={16} className="text-white/64" />
                <h3 className="font-display text-base font-bold">{step.title}</h3>
              </div>
              <p className="text-sm leading-6 text-white/54">{step.body}</p>
            </div>
          );
        })}
      </div>

      {/* Models */}
      <PriceBlock icon={Sparkles} title="Models" subtitle="Per 1M tokens through one OpenAI-compatible gateway. Auto routes pick the model for the job.">
        <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/[0.025]">
          <table className="w-full min-w-[460px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-white/40">
                <th className="px-4 py-3 font-semibold">Model</th>
                <th className="px-4 py-3 font-semibold">Provider</th>
                <th className="px-4 py-3 text-right font-semibold">Input / 1M</th>
                <th className="px-4 py-3 text-right font-semibold">Output / 1M</th>
              </tr>
            </thead>
            <tbody>
              {data.models.map((m) => (
                <tr key={m.id} data-testid={`model-price-${m.id}`} className="border-b border-white/[0.06] last:border-0">
                  <td className="px-4 py-2.5 font-mono text-[13px] text-white/82">{m.id}</td>
                  <td className="px-4 py-2.5 text-white/54">
                    <span className="inline-flex items-center gap-2">
                      {m.logo ? <img src={m.logo} alt="" width={14} height={14} className="opacity-80" loading="lazy" /> : null}
                      {m.providerName}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-white/82">{money(m.inputPer1m)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-white/82">{money(m.outputPer1m)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-white/40">
          A representative slice. <a href="/gateway" className="text-white/64 underline hover:text-white">see every model and provider</a>.
        </p>
      </PriceBlock>

      {/* Compute */}
      <PriceBlock
        icon={Server}
        title="Hardware pricing"
        subtitle="Billed per second of wall-clock from the same balance. GPU model endpoints scale to zero, so idle hardware costs nothing. Every machine is priced below Replicate's rate for the same GPU."
      >
        <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/[0.025]">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-white/40">
                <th className="px-4 py-3 font-semibold">Hardware</th>
                <th className="px-4 py-3 text-right font-semibold">Price</th>
                <th className="px-4 py-3 text-right font-semibold">GPU</th>
                <th className="px-4 py-3 text-right font-semibold">CPU</th>
                <th className="px-4 py-3 text-right font-semibold">GPU RAM</th>
                <th className="px-4 py-3 text-right font-semibold">RAM</th>
              </tr>
            </thead>
            <tbody>
              {data.compute.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`compute-price-${c.id}`}
                  className={`border-b border-white/[0.06] last:border-0 ${c.comingSoon ? 'opacity-55' : ''}`}
                >
                  <td className="px-4 py-2.5 text-white/82">
                    <span className="inline-flex items-center gap-2">
                      <Cpu size={13} className="shrink-0 text-white/40" />
                      <span>
                        {c.label}
                        {c.comingSoon && (
                          <span className="ml-2 rounded-full border border-white/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white/50">
                            coming soon
                          </span>
                        )}
                        <span className="block font-mono text-[11px] text-white/36">{c.id}</span>
                      </span>
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <span className="block font-mono text-white/82">{c.priceSecondText ? `${c.priceSecondText}/sec` : c.priceHourText}</span>
                    {c.priceSecondText && <span className="block font-mono text-xs text-white/40">{c.priceHourText}/hr</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right text-white/54">{c.gpu ? `${c.gpuCount || 1}x ${c.gpu.startsWith('TPU') ? 'TPU' : 'GPU'}` : '—'}</td>
                  <td className="px-4 py-2.5 text-right text-white/54">{c.vcpu}x</td>
                  <td className="px-4 py-2.5 text-right text-white/54">{c.vramGb ? `${c.vramGb}GB` : '—'}</td>
                  <td className="px-4 py-2.5 text-right text-white/54">{c.ramGb}GB</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-white/40">
          You pay only while a machine runs your work, metered per second. TPUs land with the Google Cloud
          integration. Google-Cloud-only hardware carries GCP's higher list prices.
        </p>
      </PriceBlock>

      {/* Search */}
      <PriceBlock icon={Search} title="Search" subtitle="Flat pricing for live web and academic search, from the same balance.">
        <div className="grid gap-3 sm:grid-cols-2">
          {data.search.map((srch) => (
            <div key={srch.id} data-testid={`search-price-${srch.id}`} className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
              <div className="flex items-baseline justify-between gap-3">
                <h4 className="font-display text-base font-bold">{srch.label}</h4>
                <span className="font-mono text-white/82">${srch.priceUsd.toFixed(2)}<span className="text-white/40"> / {srch.unit}</span></span>
              </div>
              <p className="mt-1 text-sm text-white/54">{srch.note}</p>
            </div>
          ))}
        </div>
      </PriceBlock>

      <PriceBlock icon={Image} title="Media optimization" subtitle="Cloudflare-style responsive images and AV1 video ladders, saved into your artifact filesystem.">
        <div className="grid gap-3 sm:grid-cols-2">
          {data.media?.image && (
            <div data-testid="media-price-image" className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Image size={15} className="text-white/44" />
                  <h4 className="font-display text-base font-bold">{data.media.image.label}</h4>
                </div>
                <span className="font-mono text-white/82">${data.media.image.priceUsd.toFixed(3)}</span>
              </div>
              <p className="mt-1 text-sm text-white/54">{data.media.image.unit}</p>
              <p className="mt-2 text-xs leading-5 text-white/40">{data.media.image.note}</p>
            </div>
          )}
          {data.media?.video && (
            <div data-testid="media-price-video" className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Video size={15} className="text-white/44" />
                  <h4 className="font-display text-base font-bold">{data.media.video.label}</h4>
                </div>
                <span className="font-mono text-white/82">${data.media.video.exampleUsd.toFixed(3)}</span>
              </div>
              <p className="mt-1 text-sm text-white/54">{data.media.video.exampleUnit}</p>
              <p className="mt-2 text-xs leading-5 text-white/40">{data.media.video.note}</p>
            </div>
          )}
        </div>
      </PriceBlock>
    </section>
  );
};

const PriceBlock: React.FC<{ icon: typeof Sparkles; title: string; subtitle: string; children: React.ReactNode }> = ({
  icon: Icon,
  title,
  subtitle,
  children,
}) => (
  <div className="mt-6">
    <div className="mb-1 flex items-center gap-2">
      <Icon size={15} className="text-white/48" />
      <h3 className="font-display text-lg font-bold" dangerouslySetInnerHTML={{ __html: title }} />
    </div>
    <p className="mb-3 max-w-2xl text-sm leading-6 text-white/48">{subtitle}</p>
    {children}
  </div>
);

// ---- optional subscription plans -----------------------------------------

type PlanTier = {
  id: string;
  name: string;
  priceUsd: number;
  monthlyCredits: number;
  reservedSlots: number;
  highlight?: boolean;
  blurb: string;
  perks: string[];
};

const STATIC_PLANS: PlanTier[] = [
  {
    id: 'free',
    name: 'Free',
    priceUsd: 0,
    monthlyCredits: 0,
    reservedSlots: 0,
    blurb: 'Start free. Pay only for what you use.',
    perks: ['Prepaid credits, no minimum', 'Shared worker pool', 'Every API + the CLI', 'Web, paper & agentic search'],
  },
  {
    id: 'pro',
    name: 'Pro',
    priceUsd: 20,
    monthlyCredits: 25000,
    reservedSlots: 1,
    blurb: 'For solo builders.',
    perks: ['25,000 credits / month', '1 guaranteed agent machine', 'RA1 + papers + agentic search quota', 'Priority over free tier'],
  },
  {
    id: 'ultra',
    name: 'Ultra',
    priceUsd: 60,
    monthlyCredits: 80000,
    reservedSlots: 3,
    highlight: true,
    blurb: 'For teams running agents in parallel.',
    perks: ['80,000 credits / month', '3 guaranteed machines + priority queue', 'Higher first-party product quotas', 'Best value per credit'],
  },
  {
    id: 'max',
    name: 'Max',
    priceUsd: 200,
    monthlyCredits: 280000,
    reservedSlots: 6,
    blurb: 'For heavy, always-on workloads.',
    perks: ['280,000 credits / month', '6 guaranteed machines + top priority', 'Largest RA1 / papers / search quotas', 'First-party credits across products'],
  },
];

const SubscriptionPlans: React.FC = () => {
  const [plans, setPlans] = React.useState<PlanTier[]>(STATIC_PLANS);
  const [publishableKey, setPublishableKey] = React.useState<string | undefined>(undefined);
  const flow = useSubscribeFlow(publishableKey);

  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/plans')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data?.plans) return;
        if (data.publishableKey) setPublishableKey(data.publishableKey);
        // Merge live pricing/credits onto the static presentation copy.
        setPlans((prev) =>
          prev.map((tier) => {
            const live = data.plans.find((p: { id: string }) => p.id === tier.id);
            return live ? { ...tier, priceUsd: live.priceUsd, monthlyCredits: live.monthlyCredits, reservedSlots: live.reservedSlots } : tier;
          }),
        );
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // subscribe opens the gated dialog: an unauthenticated user signs in inside the
  // dialog, then the embedded Stripe checkout mounts in place (no navigation).
  // Free → signup. On success Stripe redirects to /account?plan=success (server
  // return_url) where the purchase is verified.
  const subscribe = flow.start;
  const pendingPlan = flow.pendingPlan;

  return (
    <section data-testid="pricing-plans" className="mt-2">
      <div className="mb-3 flex items-center gap-2">
        <Sparkles size={16} className="text-white/48" />
        <h2 className="font-display text-2xl font-bold">Plans with monthly credits</h2>
      </div>
      <p className="mb-5 max-w-2xl text-sm leading-6 text-white/54">
        Plans add monthly credits at a better rate, guaranteed machines, and queue priority. Same balance: go over
        your plan and you keep paying the usage rates. No subscription required. Cancel anytime.
      </p>
      {flow.error && (
        <p className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{flow.error}</p>
      )}
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        {plans.map((plan) => (
          <article
            key={plan.id}
            data-testid={`plan-${plan.id}`}
            className={`flex flex-col rounded-xl border p-4 ${
              plan.highlight ? 'border-white/30 bg-sky-400/15' : 'border-white/10 bg-white/[0.035]'
            }`}
          >
            <div className="flex items-baseline justify-between">
              <h3 className="font-display text-lg font-bold">{plan.name}</h3>
              {plan.highlight && (
                <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-black">
                  Popular
                </span>
              )}
            </div>
            <div className="mt-2">
              <span className="font-display text-3xl font-bold">${plan.priceUsd}</span>
              <span className="text-sm text-white/48">/mo</span>
            </div>
            <p className="mt-1 text-sm text-white/54">{plan.blurb}</p>
            {plan.monthlyCredits > 0 && (
              <div className="mt-3 rounded-lg border border-white/10 bg-black px-3 py-2">
                <div className="text-sm text-white/72">{plan.monthlyCredits.toLocaleString()} credits / month</div>
                {(() => {
                  const savePct = Math.round((1 - plan.priceUsd / plan.monthlyCredits / 0.001) * 100);
                  return savePct > 0 ? (
                    <div className="mt-0.5 text-xs font-semibold text-emerald-300/80">Save {savePct}% vs pay-as-you-go</div>
                  ) : null;
                })()}
              </div>
            )}
            <ul className="mt-3 flex flex-1 flex-col gap-2 text-sm text-white/58">
              {plan.perks.map((perk) => (
                <li key={perk} className="flex items-start gap-2">
                  <Check size={15} className="mt-0.5 shrink-0 text-white/48" />
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
            <a
              href={plan.id === 'free' ? '/signup' : `/account?plan=checkout&plan_id=${encodeURIComponent(plan.id)}`}
              role="button"
              onClick={(event) => {
                event.preventDefault();
                if (pendingPlan !== plan.id) subscribe(plan.id);
              }}
              aria-disabled={pendingPlan === plan.id}
              aria-label={plan.id === 'free' ? 'Get started free' : `Subscribe to ${plan.name}`}
              className={`mt-4 inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-bold transition-colors ${pendingPlan === plan.id ? 'pointer-events-none opacity-60' : ''} ${
                plan.highlight
                  ? 'bg-sky-400 text-black hover:bg-sky-300'
                  : 'border border-white/12 bg-white/[0.04] text-white hover:bg-white hover:text-black'
              }`}
            >
              {pendingPlan === plan.id ? 'Opening…' : plan.id === 'free' ? 'Get started' : `Subscribe to ${plan.name}`}
            </a>
          </article>
        ))}
      </div>
      {flow.element}
    </section>
  );
};

export const PlatformPricing: React.FC = () => (
  <>
    <AuthProvider>
      <SubscriptionPlans />
    </AuthProvider>
    <UsageBasedPricing />
  </>
);
